import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect, useRef } from 'react';
import LocationStep from './01-LocationStep';
import DetailsStep from './02-DetailsStep';
import GeospatialAnalysisStep from './03-GeospatialAnalysisStep';
import PhotoStep from './04-PhotoStep';
import FloorPlanStep from './05-FloorPlanStep';
import './PropertyWizard.css';

const EDIT_STEPS = [
  { key: 1, label: 'موقعیت ملک' },
  { key: 2, label: 'جزئیات ملک' },
  { key: 3, label: 'تحلیل مکانی' },
  { key: 4, label: 'عکس‌ها' },
  { key: 5, label: 'پلان واحد' },
];

// Extract a bare numeric/string ID from either a raw value or a nested object
const extractId = (val) => {
  if (val === null || val === undefined) return null;
  if (typeof val === 'object') return val.id ?? null;
  return val;
};

// Normalize amenities array to { amenity: <bare id>, notes, value }
// Handles both { amenity: { id, name }, notes, value } and { amenity: id, notes, value }
const normalizeAmenities = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map(a => {
      if (typeof a !== 'object' || a === null) return { amenity: a, notes: '', value: null };
      // a.amenity may be a nested object OR a bare id
      const amenityId = extractId(a.amenity) ?? extractId(a.id) ?? null;
      return { amenity: amenityId, notes: a.notes || '', value: a.value !== undefined ? a.value : null };
    })
    .filter(a => a.amenity !== null && a.amenity !== undefined);
};

/**
 * PropertyEditModal
 *
 * Full-screen editor for steps 1–5 of an existing property.
 * - Fetches full property data on mount so all fields are pre-populated.
 * - Each step saves independently — user can jump to any tab freely.
 * - "ذخیره و بستن" closes after saving; "بستن" closes without saving.
 */
function PropertyEditModal({ property, onClose, onSaved }) {
  const [activeStep, setActiveStep] = useState(1);
  const [stepData, setStepData] = useState({ 1: {}, 2: {}, 3: {}, 4: {}, 5: {} });
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSteps, setSavedSteps] = useState(new Set());
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const token = () => localStorage.getItem('access_token');

  useEffect(() => {
    const fetchProperty = async () => {
      setLoadingData(true);
      try {
        const res = await apiRequest(
          apiRoutes.properties.detail(property.id),
          { headers: { Authorization: `Bearer ${token()}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setStepData({
            1: {
              address: data.address || '',
              city_id: extractId(data.city_id ?? data.city),
              province_id: extractId(data.province_id ?? data.province),
              region_id: extractId(data.region_id ?? data.region),
              neighborhood_id: extractId(data.neighborhood_id ?? data.neighborhood),
              postal_code: data.postal_code || '',
              location: data.location || null,
            },
            2: {
              name: data.name || '',
              description: data.description || '',
              usage: extractId(data.usage),
              property_type: extractId(data.property_type),
              establish_date: data.establish_date || '',
              area: data.area || '',
              amenities: normalizeAmenities(data.amenities),
            },
            3: {},
            4: {},
            5: {},
          });
        } else {
          setError('خطا در بارگذاری اطلاعات ملک');
        }
      } catch {
        setError('خطا در بارگذاری اطلاعات ملک');
      } finally {
        setLoadingData(false);
      }
    };
    fetchProperty();
  }, [property.id]);

  // Save the current step — stays on same step, shows success banner
  const handleStepSubmit = async (payload) => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    setStepData(prev => ({ ...prev, [activeStep]: payload }));

    try {
      let response;

      if (activeStep === 1) {
        response = await apiRequest(
          apiRoutes.propertyWizard.step(property.id, '1-location'),
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify(payload),
          }
        );
      } else if (activeStep === 2) {
        response = await apiRequest(
          apiRoutes.propertyWizard.step(property.id, '2-details'),
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify(payload),
          }
        );
      } else if (activeStep === 3) {
        response = await apiRequest(
          apiRoutes.propertyWizard.geospatialFetch(property.id),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify(payload),
          }
        );
      } else if (activeStep === 4) {
        response = await apiRequest(
          apiRoutes.propertyWizard.completeStep(property.id, '4-images'),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
            body: JSON.stringify(payload),
          }
        );
      } else if (activeStep === 5) {
        if (payload.floor_plan_saved && payload.floor_plan_image_url) {
          response = { ok: true };
        } else {
          if (!payload.plan) throw new Error('لطفاً ابتدا پلان را ذخیره کنید');
          const formData = new FormData();
          formData.append('plan', payload.plan);
          response = await apiRequest(
            apiRoutes.propertyWizard.plan(property.id),
            {
              method: 'PUT',
              headers: { Authorization: `Bearer ${token()}` },
              body: formData,
            }
          );
        }
      }

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || JSON.stringify(err));
      }

      setSavedSteps(prev => new Set([...prev, activeStep]));
      onSaved();
      if (closeAfterSave) {
        onClose();
      } else {
        setSuccessMsg('تغییرات ذخیره شد');
        setTimeout(() => setSuccessMsg(null), 2500);
      }
      setCloseAfterSave(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const contentRef = useRef(null);

  const handleSaveAndClose = () => {
    setCloseAfterSave(true);
    // Trigger the step's own save button
    const btn = contentRef.current?.querySelector('.submit-button');
    if (btn) {
      btn.click();
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 1) {
      setActiveStep(prev => prev - 1);
      setError(null);
      setSuccessMsg(null);
    }
  };

  const currentStepIndex = EDIT_STEPS.findIndex(s => s.key === activeStep);
  const progressPct = Math.round(((currentStepIndex + 1) / EDIT_STEPS.length) * 100);

  const renderStep = () => {
    if (loadingData) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, gap: 12 }}>
          <div>در حال بارگذاری اطلاعات...</div>
        </div>
      );
    }

    const save = (payload) => handleStepSubmit(payload);

    switch (activeStep) {
      case 1:
        // key forces remount when stepData[1] is freshly loaded
        return <LocationStep key={JSON.stringify(stepData[1])} onSubmit={save} initialData={stepData[1]} onPreviousStep={handlePreviousStep} />;
      case 2:
        // key forces DetailsStep to remount with fresh initialData so amenities useEffect
        // always runs with the real data, never with a stale closure
        return <DetailsStep key={loadingData ? 'loading' : 'loaded'} onSubmit={save} initialData={stepData[2]} onPreviousStep={handlePreviousStep} />;
      case 3:
        return <GeospatialAnalysisStep onSubmit={save} initialData={stepData[3]} onPreviousStep={handlePreviousStep} locationData={stepData[1]} propertyId={property.id} />;
      case 4:
        return <PhotoStep onSubmit={save} initialData={stepData[4]} onPreviousStep={handlePreviousStep} propertyId={property.id} />;
      case 5:
        return <FloorPlanStep onSubmit={save} initialData={stepData[5]} onPreviousStep={handlePreviousStep} propertyId={property.id} />;
      default:
        return null;
    }
  };

  return (
    <div className="property-edit-fullscreen">
      {/* Header */}
      <div className="property-edit-header">
        <div className="property-edit-title">
          ویرایش ملک: {property.name || `ملک ${property.id}`}
        </div>

        <div className="property-edit-tabs">
          {EDIT_STEPS.map((s, i) => (
            <button
              key={s.key}
              className={`edit-tab ${activeStep === s.key ? 'active' : ''} ${savedSteps.has(s.key) ? 'done' : ''}`}
              onClick={() => { setActiveStep(s.key); setError(null); setSuccessMsg(null); }}
              disabled={loadingData}
            >
              <span className="edit-tab-num">{i + 1}</span>
              <span className="edit-tab-label">{s.label}</span>
            </button>
          ))}
        </div>

        <button
          className="property-edit-close"
          onClick={handleSaveAndClose}
          disabled={loadingData || saving}
        >
          ذخیره و بستن
        </button>
        <button className="property-edit-close" style={{ marginRight: 8, opacity: 0.7 }} onClick={onClose}>بستن</button>
      </div>

      {/* Progress */}
      <div className="property-edit-progress">
        <div className="property-edit-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Banners */}
      {error && <div className="error-message" style={{ margin: '8px 16px 0' }}>{error}</div>}
      {successMsg && <div className="success-message" style={{ margin: '8px 16px 0' }}>{successMsg}</div>}

      {/* Saving overlay */}
      {saving && (
        <div className="loading-overlay">
          <div className="loading-text">در حال ذخیره...</div>
        </div>
      )}

      {/* Step content */}
      <div className="property-edit-content" ref={contentRef}>
        {renderStep()}
      </div>
    </div>
  );
}

export default PropertyEditModal;
